import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { fetchKpiCollectionConfigurations } from '../../services/queries/KpiCollectionQueries';
import { fetchKPITemplates } from '../../services/queries/KPITemplatesQueries';
import { kpiTemplatesListStateLoadableJ } from '../../services/state/KPI/KPITemplatesStateJ';
import { ConsolidatedTemplatesQueryKey } from './KpiCollection/CustomKpiTemplate/state/custom-template-state';
import { KpiCollectionSettingsQueryKey } from './KpiCollection/state/kpi-collection-state';

interface INamed {
  id?: number | string | null;
  name?: string | null;
}

const nameOf = (items: readonly INamed[] | undefined, id: string | undefined) =>
  id ? (items?.find((item) => String(item.id) === id)?.name ?? undefined) : undefined;

/**
 * The name the sub-nav shows for this template, read from the cache the nav already filled.
 * `enabled: false` follows the cache without ever fetching (the fetcher is named only so the query is well formed),
 * so a frosted module still makes no requests of its own.
 */
export function useConsolidatedTemplateName(templateId: string | undefined): string | undefined {
  const { data } = useQuery<INamed[]>({
    queryKey: ConsolidatedTemplatesQueryKey,
    queryFn: fetchKPITemplates,
    enabled: false,
  });
  return nameOf(data, templateId);
}

export function useKpiConfigurationName(configId: string | undefined): string | undefined {
  const { data } = useQuery<INamed[]>({
    queryKey: KpiCollectionSettingsQueryKey,
    queryFn: fetchKpiCollectionConfigurations,
    enabled: false,
  });
  return nameOf(data, configId);
}

/** The legacy templates nav reads this atom, so it is loaded whenever the route is reachable. */
export function useLegacyTemplateName(templateId: string | undefined): string | undefined {
  const templates = useAtomValue(kpiTemplatesListStateLoadableJ);
  return templates.state === 'hasData' ? nameOf(templates.data ?? undefined, templateId) : undefined;
}
